import type { FilterOption } from '@/scopes/search/types'
import { useIntl } from '@/intl'
import * as DepartureAirportFilterComponent from '@/scopes/common/components/Filters/DepartureAirportFilter'
import { mapOptions } from '@/scopes/common/components/Filters/GroupedAirportFilter/mapper'
import { OptionGroup } from '@/scopes/common/components/Filters/GroupedAirportFilter/OptionGroup'
import * as DialogPrimitive from '@/scopes/search/components/QuickSearchBar/DialogPrimitive'
import { useQuickSearchStore } from '@/scopes/search/components/QuickSearchBar/store'
import {
  Button,
  ButtonLoaderIcon,
  DropdownMultiselectContext,
  Icon,
  SpaceBetween,
  tk,
  Typography,
} from '@sunwebgroup/ui-components'
import { equals, reject } from 'ramda'
import * as React from 'react'
import { styled } from 'styled-components'
import { Item } from './QuickSearchBar'

export const Content = styled.div`
  @media ${tk('mq_tablet')} {
    padding: ${tk('spacing_lg')} ${tk('spacing_lg')} 0;
  }
`

export function GroupedDepartureAirportFilter({
  disabled,
  onConfirm,
  options,
  selectedOptions,
}: {
  onConfirm: (selection: string[]) => void
  options: FilterOption<string>[]
  selectedOptions: FilterOption<string>[]
  disabled?: boolean
}) {
  const intl = useIntl()
  const formattedSelectedValues = selectedOptions.map(o => o.value).join(', ')
  const { setActiveItem } = useQuickSearchStore()
  const [localSelectedValues, setLocalSelectedValues] = React.useState(selectedOptions.map(option => option.value))
  const onSelectedValueChange = React.useCallback((newSelectedValue: string[]) => {
    setLocalSelectedValues(newSelectedValue)
  }, [])
  const handleOptionAdd = React.useCallback(
    (addedValue: string) => {
      onSelectedValueChange?.([...localSelectedValues, addedValue])
    },
    [onSelectedValueChange, localSelectedValues],
  )

  const handleOptionRemove = React.useCallback(
    (removedValue: string) => {
      onSelectedValueChange?.(reject(equals(removedValue), localSelectedValues))
    },
    [onSelectedValueChange, localSelectedValues],
  )

  const handleOptionsAdd = React.useCallback(
    (addedValues: string[]) => {
      onSelectedValueChange?.([...localSelectedValues, ...addedValues])
    },
    [onSelectedValueChange, localSelectedValues],
  )

  const handleOptionsRemove = React.useCallback(
    (removedValues: string[]) => {
      onSelectedValueChange?.(localSelectedValues.filter(val => !removedValues.includes(val)))
    },
    [onSelectedValueChange, localSelectedValues],
  )

  React.useEffect(() => {
    setLocalSelectedValues(selectedOptions.map(option => option.value))
  }, [selectedOptions])

  const handleClose = () => {
    setActiveItem(null)
    onConfirm(localSelectedValues)
  }

  const mappedOptions = React.useMemo(() => mapOptions(options), [options])
  const context = React.useMemo(() => ({
    selectedValues: localSelectedValues,
    removeOption: handleOptionRemove,
    addOption: handleOptionAdd,
    removeOptions: handleOptionsRemove,
    addOptions: handleOptionsAdd,
  }
  ), [
    localSelectedValues,
    handleOptionRemove,
    handleOptionAdd,
    handleOptionsRemove,
    handleOptionsAdd,
  ])

  return (
    <DropdownMultiselectContext.Provider
      value={context}
    >
      <Item.Root
        id="airport-filter"
        icon="IconAirplane"
        placeholder={intl.formatMessage({
          id: 'quicksearch_filter_departure_airport_placeholder',
        })}
        title={intl.formatMessage({
          id: 'quicksearch_filter_departure_airport_title',
        })}
        onClose={handleClose}
      >
        <Item.AsPopover selectionFormattedText={formattedSelectedValues} disabled={disabled}>
          <SpaceBetween direction="y" spacing={tk('spacing_md')}>
            <Content>
              {mappedOptions.map((val) => {
                return <OptionGroup key={val.id} id={val.id} value={val.value} options={val.children || []} />
              })}
            </Content>
            <DepartureAirportFilterComponent.Footer>
              <DepartureAirportFilterComponent.CloseButton $fullSize onClick={handleClose} disabled={disabled}>
                {intl.formatMessage({ id: 'quicksearch_save_button_text' })}
              </DepartureAirportFilterComponent.CloseButton>
            </DepartureAirportFilterComponent.Footer>
          </SpaceBetween>
        </Item.AsPopover>
        <Item.AsDialog selectionFormattedText={formattedSelectedValues} disabled={disabled}>
          <DialogPrimitive.Root>
            <DialogPrimitive.Header>
              <DialogPrimitive.HeaderTitle>
                <Typography $variant="body-lg-bold">
                  {intl.formatMessage({ id: 'quicksearch_filter_departure_airport_title' })}
                </Typography>
                <Button onClick={handleClose} $hierarchy="tertiary" $withoutColor>
                  <Icon glyph="IconCrossMedium" size="md" />
                </Button>
              </DialogPrimitive.HeaderTitle>
            </DialogPrimitive.Header>
            <DialogPrimitive.Body>
              <Content>
                {mappedOptions.map((val) => {
                  return <OptionGroup key={val.id} id={val.id} value={val.value} options={val.children || []} />
                })}
              </Content>
            </DialogPrimitive.Body>
            <DialogPrimitive.Footer>
              <Button $fullSize onClick={handleClose} disabled={disabled}>
                {disabled && <ButtonLoaderIcon />}
                {intl.formatMessage({ id: 'quicksearch_save_button_text' })}
              </Button>
            </DialogPrimitive.Footer>
          </DialogPrimitive.Root>
        </Item.AsDialog>
        <Item.AsIcon
          selectionFormattedText={formattedSelectedValues}
          compactSelectionFormattedText={formattedSelectedValues}
        />
      </Item.Root>
    </DropdownMultiselectContext.Provider>
  )
}
